/**
 * DataPagedTable — paginated HTML <table> for structured-data results.
 *
 * Pure presentational primitive (PR-A2). Reused by:
 *   1. ``<StructuredDataCard>``    — inline chat result, 10/page (this PR)
 *   2. ``<EnterpriseDataPanel>``   — inline route panel, 15/page (PR-A3)
 *   3. ``ChartDetailModal``        — Stream A PR-4 detail surface
 *
 * Behavior contract:
 *   - Header row + sticky FIRST column (so wide tables stay scannable).
 *   - Horizontal scroll inside the table when > 6 columns.
 *   - Footer pagination: Prev / "Pagina X di Y" / Next.
 *   - Per-cell formatting:
 *       null/undefined   → "—"
 *       number           → ``it-IT`` locale (tabular-nums)
 *       boolean          → "sì" / "no"
 *       object           → ``JSON.stringify`` truncated at 60 chars
 *       everything else  → String(value)
 *   - Empty data → ``"Nessuna riga restituita"`` empty state.
 *   - When ``data`` shrinks below the current page, snaps back to the last
 *     valid page so the footer never reads "Pagina 5 di 3".
 *
 * Storybook-friendly: zero imports from ``@/features/*``.
 */

import type { I18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight } from "lucide-react";
import * as React from "react";
import { cn } from "../lib/cn";
import { Button } from "../primitives/button";

const DEFAULT_PAGE_SIZE = 10;
const MAX_COLS_BEFORE_SCROLL = 6;
const OBJECT_TRUNCATE_AT = 60;
/** Data values follow Italian conventions regardless of the UI language. */
const DATA_LOCALE = "it-IT";
/** Decimal strings as emitted by SQL Server ``numeric`` (e.g. "72.950000000000"); codes like "00094" stay text. */
const DECIMAL_STRING = /^-?\d+\.\d+$/;
/** Year columns render as plain digits ("2025", not "2.025"). */
const YEAR_COLUMN = /^(anno|esercizio|year|yr|fiscal_?year)$/i;

export interface DataColumnMeta {
  readonly name: string;
  readonly label?: string;
  readonly type?: string;
  readonly semantic_type?: string;
  readonly format?: string;
  readonly currency?: string;
}

export interface DataPagedTableProps {
  /** Rows to render. Empty array → "Nessuna riga restituita" empty state. */
  readonly data: ReadonlyArray<Record<string, unknown>>;
  /**
   * Explicit column order. When empty the primitive falls back to
   * ``Object.keys(data[0])`` so callers can pass-through wire payloads
   * directly.
   */
  readonly columns: ReadonlyArray<string>;
  /** Rows per page. Default 10 (matches V1 inline-card behaviour). */
  readonly pageSize?: number;
  /** Optional extra classes appended to the root container. */
  readonly className?: string;
  /** Semantic presentation metadata emitted by the structured-data backend. */
  readonly columnMeta?: ReadonlyArray<DataColumnMeta>;
}

/**
 * Pure cell formatter — exported only for unit tests (callers should pass
 * raw values; the table owns the rendering choices).
 */
export function formatCellValue(value: unknown, i18n?: I18n, meta?: DataColumnMeta, column?: string): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "number" && !Number.isFinite(value)) return "—";
  const numericValue =
    typeof value === "number"
      ? value
      : typeof value === "string" && (meta?.type === "numeric" || DECIMAL_STRING.test(value))
        ? Number(value)
        : Number.NaN;
  if (Number.isFinite(numericValue)) {
    const locale = DATA_LOCALE;
    if (Number.isInteger(numericValue) && YEAR_COLUMN.test(column ?? meta?.name ?? "")) return String(numericValue);
    if (meta?.semantic_type === "monetary" && meta.currency) {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: meta.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(numericValue);
    }
    if (meta?.format === "decimal_2") {
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(numericValue);
    }
    return numericValue.toLocaleString(locale);
  }
  if (typeof value === "boolean") {
    if (i18n) return value ? i18n._(msg`sì`) : i18n._(msg`no`);
    return value ? "sì" : "no";
  }
  if (typeof value === "string") return value;
  try {
    const json = JSON.stringify(value);
    return json.length > OBJECT_TRUNCATE_AT ? `${json.slice(0, OBJECT_TRUNCATE_AT)}…` : json;
  } catch {
    return String(value);
  }
}

export const DataPagedTable: React.FC<DataPagedTableProps> = ({
  data,
  columns,
  pageSize = DEFAULT_PAGE_SIZE,
  className,
  columnMeta = [],
}) => {
  const { i18n } = useLingui();
  const resolvedColumns = React.useMemo<ReadonlyArray<string>>(() => {
    if (columns.length > 0) return columns;
    if (data.length === 0) return [];
    return Object.keys(data[0] ?? {});
  }, [columns, data]);

  const totalRows = data.length;
  const metaByColumn = React.useMemo(
    () => new Map(columnMeta.map((meta) => [meta.name, meta])),
    [columnMeta],
  );
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));

  const [page, setPage] = React.useState(0);
  const [sort, setSort] = React.useState<{
    readonly column: string;
    readonly direction: "asc" | "desc";
  } | null>(null);

  const sortedRows = React.useMemo(() => {
    if (!sort) return data;
    const indexed = data.map((row, index) => ({ row, index }));
    indexed.sort((left, right) => {
      const a = left.row[sort.column];
      const b = right.row[sort.column];
      if (a == null && b == null) return left.index - right.index;
      if (a == null) return 1;
      if (b == null) return -1;
      const numericA = typeof a === "number" ? a : Number.NaN;
      const numericB = typeof b === "number" ? b : Number.NaN;
      const comparison =
        Number.isFinite(numericA) && Number.isFinite(numericB)
          ? numericA - numericB
          : String(a).localeCompare(String(b), i18n.locale, {
              numeric: true,
              sensitivity: "base",
            });
      if (comparison === 0) return left.index - right.index;
      return sort.direction === "asc" ? comparison : -comparison;
    });
    return indexed.map(({ row }) => row);
  }, [data, i18n.locale, sort]);

  // Re-clamp the active page if the data set shrinks (re-execute SQL,
  // server filter, etc) — never let the footer read "Pagina 5 di 3".
  React.useEffect(() => {
    setPage((current) => Math.min(current, totalPages - 1));
  }, [totalPages]);

  const pageRows = React.useMemo(() => {
    const start = page * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, page, pageSize]);

  const needsHorizontalScroll = resolvedColumns.length > MAX_COLS_BEFORE_SCROLL;

  if (totalRows === 0) {
    return (
      <div
        className={cn(
          "rounded-md border border-hairline bg-cream-deep/30 px-3 py-6",
          "text-center text-[11px] text-ink-3",
          className,
        )}
      >
        {i18n._(msg`Nessuna riga restituita`)}
      </div>
    );
  }

  const handlePrev = () => setPage((p) => Math.max(0, p - 1));
  const handleNext = () => setPage((p) => Math.min(totalPages - 1, p + 1));
  const handleSort = (column: string) => {
    setSort((current) =>
      current?.column === column
        ? { column, direction: current.direction === "asc" ? "desc" : "asc" }
        : { column, direction: "asc" },
    );
    setPage(0);
  };

  return (
    <div
      className={cn("flex flex-col gap-2 rounded-md border border-hairline bg-cream", className)}
    >
      <div
        className={cn("min-w-0", needsHorizontalScroll ? "overflow-x-auto" : "overflow-x-hidden")}
      >
        <table className="w-full border-collapse text-[11px]">
          <thead className="bg-cream-deep/60">
            <tr className="border-hairline border-b">
              {resolvedColumns.map((col, colIdx) => (
                <th
                  key={col}
                  scope="col"
                  aria-sort={
                    sort?.column === col
                      ? sort.direction === "asc"
                        ? "ascending"
                        : "descending"
                      : "none"
                  }
                  className={cn(
                    "px-2 py-2 font-medium text-[11px] text-ink-3",
                    "whitespace-nowrap",
                    metaByColumn.get(col)?.type === "numeric" ? "text-right" : "text-left",
                    colIdx === 0 ? "sticky left-0 z-10 bg-cream-deep/95 backdrop-blur-sm" : null,
                  )}
                >
                  <button
                    type="button"
                    onClick={() => handleSort(col)}
                    className={cn(
                      "inline-flex w-full items-center justify-between gap-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/40",
                      metaByColumn.get(col)?.type === "numeric" ? "text-right" : "text-left",
                    )}
                    aria-label={i18n._(msg`Ordina per ${metaByColumn.get(col)?.label ?? col}`)}
                  >
                    <span>{metaByColumn.get(col)?.label ?? col.replaceAll("_", " ")}</span>
                    {sort?.column === col ? (
                      sort.direction === "asc" ? (
                        <ArrowUp size={11} aria-hidden />
                      ) : (
                        <ArrowDown size={11} aria-hidden />
                      )
                    ) : (
                      <ArrowUpDown size={11} aria-hidden className="opacity-45" />
                    )}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row, rowIdx) => {
              // Composite stable key — first column value + page-local index.
              // Wire rows don't carry a generic id, so we can't key by id; the
              // composite avoids ``noArrayIndexKey`` lint without falsely
              // claiming uniqueness when the first column repeats.
              const firstColName = resolvedColumns[0];
              const firstVal = firstColName ? row[firstColName] : undefined;
              const rowKey = `${page}-${formatCellValue(firstVal, i18n)}-${rowIdx}`;
              return (
                <tr key={rowKey} className="border-hairline border-b last:border-b-0">
                  {resolvedColumns.map((col, colIdx) => {
                    const meta = metaByColumn.get(col);
                    const formatted = formatCellValue(row[col], i18n, meta, col);
                    return (
                      <td
                        key={col}
                        className={cn(
                          "max-w-[200px] truncate px-2 py-1.5 text-ink-2 tabular-nums",
                          meta?.type === "numeric" ? "text-right" : "text-left",
                          colIdx === 0
                            ? "sticky left-0 z-[1] bg-cream font-medium text-ink-1"
                            : null,
                        )}
                        title={formatted}
                      >
                        {formatted}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {totalPages > 1 ? (
        <footer className="flex items-center justify-between px-2 pt-1 pb-2">
          <span className="text-[10px] text-ink-3">
            {i18n._(msg`${totalRows.toLocaleString("it-IT")} righe`)}
          </span>
          <div className="flex items-center gap-1.5">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={i18n._(msg`Pagina precedente`)}
              disabled={page === 0}
              onClick={handlePrev}
            >
              <ChevronLeft aria-hidden />
            </Button>
            <span
              className="px-1 text-[11px] text-ink-2 tabular-nums"
              aria-live="polite"
              aria-atomic="true"
            >
              {i18n._(msg`Pagina ${page + 1} di ${totalPages}`)}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={i18n._(msg`Pagina successiva`)}
              disabled={page >= totalPages - 1}
              onClick={handleNext}
            >
              <ChevronRight aria-hidden />
            </Button>
          </div>
        </footer>
      ) : null}
    </div>
  );
};
DataPagedTable.displayName = "DataPagedTable";
